import '@protoss/ui/src/components/auth/auth.css';
import '@protoss/ui/src/components/components.css';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { App } from './App';
import { PublicAppConfigProvider } from './contexts/PublicAppConfigContext';
import { signInWithCustomToken } from 'firebase/auth';
import { consumeDesktopHandoff } from './lib/desktopHandoff';
import { firebaseAuth, initFirebase } from './lib/firebase';
import { setPreAuthConfig } from './lib/preAuthConfig';
import { initTheme } from './lib/theme';
import './styles/global.css';
import './styles/page-tabs.css';
import './styles/doc-contract-view.css';
import './styles/notifications.css';
// AI chat render styles - loaded app-wide so the chat (activity chain, status dots, narration,
// interaction drawer) renders identically on every surface (deal / project / home / live-meeting),
// not just the project page where these rules used to live.
import './features/ai-chat/ai-chat.css';

initTheme();

async function bootstrap() {
  // Fetch Firebase client config from the API before mounting React.
  // The config lives in workspace.config (domain='idp') so it never needs to
  // be baked into the Vite build as VITE_FIREBASE_* environment variables.
  //
  // In local dev (?workspace=<slug> URL param) we pass the slug so the API
  // returns the config for that specific workspace instead of the first one.
  try {
    const configUrl = (() => {
      if (import.meta.env.DEV) {
        const slug = new URLSearchParams(window.location.search).get('workspace');
        if (slug) return `/api/auth/firebase-client-config?workspace=${encodeURIComponent(slug)}`;
      }
      return '/api/auth/firebase-client-config';
    })();
    const res = await fetch(configUrl);
    if (res.ok) {
      const { firebaseConfig, workspaceSlug, allowedMethods, productName, brandConfig } =
        (await res.json()) as {
          firebaseConfig: {
            apiKey: string;
            authDomain: string;
            projectId: string;
            storageBucket?: string;
            messagingSenderId?: string;
            appId: string;
          };
          workspaceSlug?: string;
          allowedMethods?: string[];
          productName?: string;
          brandConfig?: import('@protoss/shared').PublicAppConfig;
        };
      initFirebase(firebaseConfig);
      setPreAuthConfig({ allowedMethods, workspaceSlug, productName, brandConfig });
    } else {
      console.error('[bootstrap] Failed to load Firebase config:', res.status);
    }
  } catch (err) {
    console.error('[bootstrap] Failed to fetch Firebase config:', err);
  }

  // Desktop app handoff (`#desktop=<customToken>`): sign in BEFORE React mounts, so the
  // first auth state ProtectedRoute sees is the signed-in user, not a redirect to /signin.
  // Clears the hash first; any failure falls through to the normal sign-in page.
  if (firebaseAuth) {
    await consumeDesktopHandoff({
      location: window.location,
      replaceState: (url) => window.history.replaceState(window.history.state, '', url),
      signIn: (token) => signInWithCustomToken(firebaseAuth, token),
    });
  }

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <PublicAppConfigProvider>
        <App />
      </PublicAppConfigProvider>
    </React.StrictMode>
  );
}

void bootstrap();
